import { Box, Card, CardContent, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import ColouredLabelBox from 'domains/core/components/ColouredLabelBox';
import type { AccessCentreRole } from 'domains/accessCentre/types';

const CARD_TITLE = 'Select New Role';
const CURRENT_LABEL = 'Current';

const cardRootStyles = {
    width: '100%',
};

const roleCardStyles = {
    width: '100%',
    cursor: 'pointer',
};

const roleCardContentStyles = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    '&:last-child': {
        pb: 2,
    },
};

const radioWrapperStyles = {
    flexShrink: 0,
    pl: 2,
    pr: 2,
    py: 0.5,
};

const roleInfoStackStyles = {
    flex: 1,
    minWidth: 0,
};

export type SelectNewRoleCardProps = {
    roles: AccessCentreRole[];
    currentRoleId: number;
    selectedRoleId: number | null;
    onSelectRole: (roleId: number) => void;
};

const SelectNewRoleCard = ({ roles, currentRoleId, selectedRoleId, onSelectRole }: SelectNewRoleCardProps) => (
    <Card variant="outlined" sx={cardRootStyles} data-testid="access-center-select-new-role-card">
        <CardContent>
            <Typography variant="h6" component="h2" gutterBottom>
                {CARD_TITLE}
            </Typography>
            <RadioGroup
                value={selectedRoleId ?? currentRoleId}
                onChange={(_, value) => onSelectRole(Number(value))}
                data-testid="access-center-role-radio-group"
            >
                <Stack spacing={1} sx={{ mt: 1 }}>
                    {roles.map((role) => {
                        const isCurrent = role.id === currentRoleId;
                        const isSelected = (selectedRoleId ?? currentRoleId) === role.id;
                        return (
                            <Card
                                key={role.id}
                                variant="outlined"
                                sx={{
                                    ...roleCardStyles,
                                    borderColor: isSelected ? 'primary.main' : undefined,
                                    borderWidth: isSelected ? 2 : 1,
                                }}
                                onClick={() => onSelectRole(role.id)}
                                data-testid={`access-center-role-option-${role.id}`}
                            >
                                <CardContent sx={roleCardContentStyles}>
                                    <Stack sx={roleInfoStackStyles} spacing={0.5}>
                                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                            <Typography variant="body1" fontWeight={500}>
                                                {role.name}
                                            </Typography>
                                            {isCurrent && (
                                                <ColouredLabelBox
                                                    color="default"
                                                    dataTestId={`access-center-role-current-badge-${role.id}`}
                                                >
                                                    {CURRENT_LABEL}
                                                </ColouredLabelBox>
                                            )}
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary">
                                            {role.description || '—'}
                                        </Typography>
                                    </Stack>
                                    <Box sx={radioWrapperStyles}>
                                        <Radio
                                            value={role.id}
                                            checked={isSelected}
                                            inputProps={{
                                                'aria-label': `Select role ${role.name}`,
                                            }}
                                            data-testid={`access-center-role-radio-${role.id}`}
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        );
                    })}
                </Stack>
            </RadioGroup>
        </CardContent>
    </Card>
);

export default SelectNewRoleCard;
